/**
 * The institutional Drive container affordance (ADR 0025 / issue #616).
 *
 * One place per course, and one per session within it. It is an affordance and
 * never a dependency: manual URLs and picker-selected references keep working
 * with no container present, so every failure here degrades to the material
 * surface the instructor already has.
 *
 * The backend answers 404 for a tenant the capability is not enabled for, so an
 * absent listing renders nothing at all rather than an error. A disabled tenant
 * must not learn the capability exists.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, FolderPlus } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { ApiError } from "@/lib/api/client";
import {
  driveContainersApi,
  driveContainersQueryKey,
  driveFolderUrl,
} from "@/lib/api/driveContainers";

import { containerFor, parentReady, type Scope } from "./drive-container-summary";

export function DriveContainerAffordance({
  scope,
  canEdit,
}: {
  scope: Scope;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);
  const containers = useQuery({
    queryKey: driveContainersQueryKey(scope.courseId),
    queryFn: () => driveContainersApi.list(scope.courseId),
    // A 404 is "this tenant has no container surface", not a fault. Retrying it
    // would be noise on every course page of every tenant that is not enabled.
    retry: false,
  });

  const create = useMutation({
    mutationFn: () =>
      scope.kind === "course"
        ? driveContainersApi.createForCourse(scope.courseId, crypto.randomUUID())
        : driveContainersApi.createForSession(scope.sessionId),
    onSuccess: async () => {
      setFailure(null);
      await queryClient.invalidateQueries({ queryKey: driveContainersQueryKey(scope.courseId) });
    },
    onError: (error: unknown) => {
      // Bounded copy. The backend's refusals are already audience-safe and
      // disclose no provider data; anything else becomes one generic sentence.
      setFailure(
        error instanceof ApiError && error.status >= 400 && error.status < 500
          ? error.message
          : "The course folder could not be created. The material links below still work.",
      );
    },
  });

  // An error leaves `data` undefined, which is the 404 case above: render
  // nothing rather than an error a disabled tenant should never see. A
  // response without the array is treated the same way rather than trusted:
  // this component decides whether an irreversible grant is offered, so it
  // does not assume a shape it did not check.
  if (containers.isLoading || !Array.isArray(containers.data?.containers)) return null;

  const all = containers.data.containers;
  const existing = containerFor(all, scope);
  const label = scope.kind === "course" ? "Course folder" : "Session folder";

  if (existing?.state === "ready" && existing.drive_folder_id) {
    return (
      <div className="flex flex-col gap-1">
        <a
          className="inline-flex items-center gap-1.5 text-ui text-muted-foreground transition-colors duration-[var(--motion-fast)] hover:text-foreground"
          href={driveFolderUrl(existing.drive_folder_id)}
          target="_blank"
          rel="noreferrer"
        >
          <ExternalLink aria-hidden="true" className="size-3.5" strokeWidth={1.75} />
          {label} in Drive
          <span className="sr-only">(opens in Google Drive in a new tab)</span>
        </a>
        <p className="text-ui text-muted-foreground">
          Create and edit teaching materials in Google Drive. If editing is unavailable,
          ask your administrator for editing access to this course folder.
          Everyone in the Workspace can open its contents by link. Keep private learner work elsewhere.
        </p>
      </div>
    );
  }

  if (!canEdit) return null;

  if (existing?.state === "unknown") {
    // Deliberately not offering a retry. ADR 0017: an unknown outcome stops
    // automation, and reconciliation is an administrator's action.
    return (
      <p className="text-ui text-muted-foreground">
        The {label.toLowerCase()} outcome is unknown. An administrator must reconcile
        it before another can be created.
      </p>
    );
  }

  if (scope.kind === "session" && !parentReady(all)) {
    return (
      <p className="text-ui text-muted-foreground">
        Create the course folder first; session folders live inside it.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="self-start"
        disabled={create.isPending || existing?.state === "pending"}
        onClick={() => create.mutate()}
      >
        <FolderPlus aria-hidden="true" className="size-4" strokeWidth={1.75} />
        {create.isPending ? `Creating ${label.toLowerCase()}…` : `Create ${label.toLowerCase()}`}
      </Button>
      {failure ? (
        <p className="text-ui text-destructive" role="status">
          {failure}
        </p>
      ) : null}
      <p className="text-ui text-muted-foreground">
        Everyone in the Workspace can open this folder by link. Its audience cannot be
        narrowed later, so do not put learner work in it. Editing access is managed separately in Drive.
      </p>
    </div>
  );
}
